// frontend/src/pages/WidgetPage.js
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-25
// Updated:      2026-09-07
//
// Description:  Displays a list of available widgets using the central registry.
//
// Mobile Responsiveness (2026-09-07):
//               • Desktop retains the existing sortable table.
//               • Mobile renders each registry entry as a touch-friendly card.
//               • Mobile sorting is handled through compact field/direction controls.
//               • Existing WidgetRegistry data and dashboard links remain unchanged.

import React, { useEffect, useState } from "react";
import { useTheme } from "../context/ThemeContext.jsx";
import WidgetRegistry from "../widgets/WidgetRegistry";

const MOBILE_BREAKPOINT = 768;

function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const apply = () => setIsMobile(mediaQuery.matches);

    apply();

    if (mediaQuery.addEventListener) mediaQuery.addEventListener("change", apply);
    else mediaQuery.addListener(apply);

    return () => {
      if (mediaQuery.removeEventListener) mediaQuery.removeEventListener("change", apply);
      else mediaQuery.removeListener(apply);
    };
  }, [breakpoint]);

  return isMobile;
}

const WidgetPage = () => {
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortedWidgets = [...WidgetRegistry].sort((a, b) => {
    if (!sortField) return 0;
    const aValue = a[sortField]?.toString().toLowerCase();
    const bValue = b[sortField]?.toString().toLowerCase();
    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const dashboardLinks = (widget) => {
    if (!widget.dashboards || widget.dashboards.length === 0) {
      return <span className="text-gray-400">—</span>;
    }

    return widget.dashboards.map((dash, index) => (
      <React.Fragment key={`${widget.id}-${dash}-${index}`}>
        <a
          href={dash === "dashboard" ? "/dashboard" : `/dashboard/${dash}`}
          className="text-[var(--secondary-color)] hover:underline"
        >
          {dash.charAt(0).toUpperCase() + dash.slice(1)}
        </a>
        {index < widget.dashboards.length - 1 ? ", " : ""}
      </React.Fragment>
    ));
  };

  return (
    <div
      className={isMobile ? undefined : "p-6"}
      style={{
        backgroundColor: "#f0f0f0",
        minHeight: isMobile ? "100%" : "100vh",
        paddingLeft: isMobile ? 12 : 24,
        paddingRight: isMobile ? 12 : 24,
        paddingTop: isMobile ? 8 : undefined,
        paddingBottom: isMobile ? 16 : undefined,
        boxSizing: "border-box",
        overflowX: "hidden",
        fontFamily: theme.font_body,
      }}
    >
      <div
        style={{
          display: isMobile ? "flex" : "block",
          flexDirection: isMobile ? "column" : undefined,
          gap: isMobile ? 10 : undefined,
          marginBottom: isMobile ? 12 : undefined,
        }}
      >
        <h2
          className={isMobile ? undefined : "text-2xl font-semibold"}
          style={{
            fontFamily: theme.font_header,
            marginTop: 0,
            marginBottom: isMobile ? 0 : "20px",
            fontSize: isMobile ? "1.35rem" : undefined,
            fontWeight: 600,
          }}
        >
          Widget Library
        </h2>

        {isMobile && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) auto",
              gap: 8,
              width: "100%",
            }}
          >
            <select
              value={sortField || ""}
              onChange={(e) => {
                const value = e.target.value || null;
                setSortField(value);
                if (value) setSortDirection("asc");
              }}
              aria-label="Sort widgets by"
              style={{
                width: "100%",
                minWidth: 0,
                height: 44,
                border: "1px solid #ccc",
                borderRadius: 8,
                background: "#fff",
                padding: "0 10px",
                fontSize: 16,
                fontWeight: 700,
              }}
            >
              <option value="">Registry order</option>
              <option value="id">Name</option>
              <option value="friendly_name">Friendly Name</option>
              <option value="description">Description</option>
              <option value="category">Category</option>
              <option value="dashboards">Count</option>
            </select>

            <button
              type="button"
              disabled={!sortField}
              onClick={() => setSortDirection((current) => (current === "asc" ? "desc" : "asc"))}
              style={{
                minWidth: 48,
                height: 44,
                borderRadius: 8,
                border: "1px solid #ccc",
                background: "#fff",
                color: "#333",
                fontWeight: 900,
                fontSize: 18,
                cursor: sortField ? "pointer" : "default",
                opacity: sortField ? 1 : 0.45,
              }}
              aria-label={sortDirection === "asc" ? "Sort descending" : "Sort ascending"}
              title={sortDirection === "asc" ? "Ascending" : "Descending"}
            >
              {sortDirection === "asc" ? "↑" : "↓"}
            </button>
          </div>
        )}
      </div>

      <div
        style={{
          background: "#fff",
          padding: isMobile ? 10 : "20px",
          borderRadius: "10px",
          border: "1px solid #ddd",
          boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          minWidth: 0,
        }}
      >
        {isMobile ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {sortedWidgets.length === 0 ? (
              <div style={{ padding: 18, textAlign: "center", color: "#6b7280" }}>
                No widgets registered.
              </div>
            ) : (
              sortedWidgets.map((widget) => (
                <article
                  key={widget.id}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    background: "#fff",
                    padding: 12,
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 10,
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 900,
                          color: "#111827",
                          wordBreak: "break-word",
                        }}
                      >
                        {widget.friendly_name || widget.id}
                      </div>
                      <div
                        style={{
                          marginTop: 3,
                          fontFamily: "monospace",
                          fontSize: 11,
                          color: "#64748b",
                          wordBreak: "break-word",
                        }}
                      >
                        {widget.id}
                      </div>
                    </div>

                    <span
                      style={{
                        flex: "0 0 auto",
                        minWidth: 32,
                        padding: "5px 8px",
                        borderRadius: 999,
                        background: "#f3f4f6",
                        color: "#374151",
                        textAlign: "center",
                        fontSize: 12,
                        fontWeight: 900,
                      }}
                      title="Dashboard count"
                    >
                      {widget.dashboards?.length || 0}
                    </span>
                  </div>

                  <div style={{ marginTop: 10, color: "#4b5563", fontSize: 13, lineHeight: 1.5 }}>
                    {widget.description || "—"}
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "90px minmax(0, 1fr)",
                      gap: "7px 10px",
                      marginTop: 12,
                      fontSize: 13,
                    }}
                  >
                    <div style={{ fontWeight: 800, color: "#64748b" }}>Category</div>
                    <div style={{ minWidth: 0, wordBreak: "break-word" }}>{widget.category || "—"}</div>

                    <div style={{ fontWeight: 800, color: "#64748b" }}>Used by</div>
                    <div style={{ minWidth: 0, lineHeight: 1.6 }}>{dashboardLinks(widget)}</div>
                  </div>
                </article>
              ))
            )}
          </div>
        ) : (
          <table className="w-full text-sm text-left text-gray-700">
            <thead>
              <tr className="bg-accent text-white">
                <th className="px-4 py-2 cursor-pointer" onClick={() => toggleSort("id")}>Name</th>
                <th className="px-4 py-2 cursor-pointer" onClick={() => toggleSort("friendly_name")}>Friendly Name</th>
                <th className="px-4 py-2 cursor-pointer" onClick={() => toggleSort("description")}>Description</th>
                <th className="px-4 py-2 cursor-pointer" onClick={() => toggleSort("category")}>Category</th>
                <th className="px-4 py-2">Used by Dashboard</th>
                <th className="px-4 py-2 cursor-pointer" onClick={() => toggleSort("dashboards")}>Count</th>
              </tr>
            </thead>
            <tbody>
              {sortedWidgets.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-4 text-center text-gray-500">
                    No widgets registered.
                  </td>
                </tr>
              ) : (
                sortedWidgets.map((widget) => (
                  <tr key={widget.id} className="border-b">
                    <td className="px-4 py-2">{widget.id}</td>
                    <td className="px-4 py-2">{widget.friendly_name}</td>
                    <td className="px-4 py-2">{widget.description || "—"}</td>
                    <td className="px-4 py-2">{widget.category}</td>
                    <td className="px-4 py-2">{dashboardLinks(widget)}</td>
                    <td className="px-4 py-2 text-center">{widget.dashboards?.length || 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default WidgetPage;
